import { useState } from 'react';
import { USD, int, magnitude, shortDate } from '../../lib/format';
import { C, EASE_SPRING, ink, text } from '../../lib/theme';
import { IconAlert, IconChevronDown, IconTrendDown, IconX } from '../icons';
import type { TopMotivoRow } from '../../lib/types';
import { ChartEmpty } from './shared';

/**
 * Gráfico 4 — Top motivos (PRD §9.4): lista ordenada com barra proporcional.
 * Não precisa ser gráfico sofisticado — é um ranking. Motivos de exceção levam
 * selo de texto, não cor (mesma regra do Reason Classifier).
 *
 * Drill-down (quadro executivo, 2026-09-11): clicar no motivo expande a
 * lista de clientes daquele motivo (já vem agregada do backend, sem fetch
 * extra), e cada cliente abre o Client Drawer — o caminho natural motivo →
 * cliente → perfil, que antes não existia (o valor aparecia mas não dava pra
 * chegar em quem era). O filtro global por motivo (`onPick`) saiu do clique
 * da linha e virou um botão próprio, senão expandir e filtrar disputariam o
 * mesmo gesto.
 */
export function TopMotivos({ rows, onPick, onOpenClient }: {
  rows: TopMotivoRow[];
  onPick?: (reasonId: number) => void;
  onOpenClient?: (masterId: string) => void;
}) {
  const [open, setOpen] = useState<number | null>(null);

  if (rows.length === 0) {
    return <ChartEmpty text="Nenhum evento confirmado e classificado ainda — o ranking aparece conforme a fila for auditada com motivo." />;
  }

  const sorted = [...rows].sort((a, b) => magnitude(b.value) - magnitude(a.value));
  const max = magnitude(sorted[0]?.value) || 1;
  const total = sorted.reduce((s, r) => s + magnitude(r.value), 0) || 1;

  return (
    <div style={{ height: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 9 }}>
      {sorted.map((r) => {
        const v = magnitude(r.value);
        const isOpen = open === r.reason_id;
        const clients = r.clients ?? [];
        return (
          <div key={r.reason_id}>
            <div
              onClick={() => setOpen(isOpen ? null : r.reason_id)}
              style={{ cursor: 'pointer', borderRadius: 10, padding: '4px 6px', margin: '0 -6px', background: isOpen ? ink(0.04) : 'transparent', transition: 'background 0.15s' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, flex: 'none',
                  color: ink(0.4), transform: isOpen ? 'rotate(180deg)' : 'none', transition: `transform 0.2s ${EASE_SPRING}`,
                }}><IconChevronDown size={11} /></span>
                <span style={{ fontSize: 12, fontWeight: 650, color: ink(0.85), flex: 1, minWidth: 0 }}>{r.label}</span>
                {r.is_exception && (
                  <span style={{
                    fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
                    padding: '1px 5px', borderRadius: 5, background: ink(0.1),
                    border: `1px solid ${ink(0.18)}`, color: ink(0.6), flex: 'none',
                  }}><IconAlert size={11} />exceção</span>
                )}
                {int(r.churn_count) > 0 && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 10.5, fontWeight: 700, color: C.churn, flex: 'none' }}>
                    <IconX size={9} />{r.churn_count}
                  </span>
                )}
                {int(r.contraction_count) > 0 && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 10.5, fontWeight: 700, color: C.amber, flex: 'none' }}>
                    <IconTrendDown size={9} />{r.contraction_count}
                  </span>
                )}
                <span style={{ fontSize: 11, color: ink(0.35), flex: 'none', fontVariantNumeric: 'tabular-nums' }}>{r.count}</span>
                <span style={{
                  fontSize: 12.5, fontWeight: 750, color: ink(0.9), flex: 'none',
                  fontVariantNumeric: 'tabular-nums', minWidth: 92, textAlign: 'right',
                }}>{USD(v)}</span>
              </div>
              <div style={{ height: 6, borderRadius: 99, background: ink(0.06), overflow: 'hidden' }}>
                <div style={{
                  width: `${(v / max) * 100}%`, height: '100%', borderRadius: 99,
                  background: r.is_exception ? ink(0.3) : `linear-gradient(90deg, ${C.accentDeep}, ${C.accent})`,
                  transition: 'width 0.4s ease',
                }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, color: ink(0.28), marginTop: 2 }}>
                <span>{((v / total) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% do total classificado</span>
                {onPick && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onPick(r.reason_id); }}
                    style={{
                      marginLeft: 'auto', border: `1px solid ${ink(0.14)}`, background: 'transparent', color: ink(0.5),
                      cursor: 'pointer', font: 'inherit', fontSize: 9.5, fontWeight: 700, padding: '1px 7px', borderRadius: 6,
                    }}
                  >filtrar</button>
                )}
              </div>
            </div>

            {isOpen && (
              <div style={{
                marginTop: 6, marginLeft: 22, display: 'flex', flexDirection: 'column', gap: 3,
                animation: `koSlideIn 0.18s ${EASE_SPRING}`,
              }}>
                {clients.map((c, i) => {
                  const cv = magnitude(c.value);
                  const isChurn = c.event_type === 'churn';
                  return (
                    <button
                      key={`${c.master_id}-${c.event_date}-${i}`}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onOpenClient?.(c.master_id); }}
                      className="ko-row"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                        border: 'none', background: 'transparent', padding: '5px 8px', borderRadius: 8,
                        cursor: onOpenClient ? 'pointer' : 'default', font: 'inherit',
                      }}
                    >
                      <span style={{ color: isChurn ? C.churn : C.amber, display: 'flex', flex: 'none' }}>
                        {isChurn ? <IconX size={10} /> : <IconTrendDown size={11} />}
                      </span>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: C.fg, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {c.master_id}
                      </span>
                      <span style={{ fontSize: 10, color: text.muted, flex: 'none' }}>{shortDate(c.event_date)}</span>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: ink(0.8), flex: 'none', fontVariantNumeric: 'tabular-nums', minWidth: 70, textAlign: 'right' }}>
                        {USD(cv)}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
